import { Component, inject, input, OnInit, TemplateRef, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { take } from 'rxjs/operators';

import { OcJsonSchemaValidatorAjv } from '../../../../../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IError } from '@orgacard/login';

import { AppService } from '../../../../../app.service';
import { CoreService } from '../../../../../core/core.service';
import { EnvironmentService } from '../../../../../core/environment.service';
import { ocValidator } from '../../../../../core/oc-validator/oc.validator';
import { ModalService } from '../../../../../core/services/modal.service';
import { UtilityHelperService } from '../../../../../core/utility-helper.service';
import { LoadingOverlayService } from '../../../../../loading-overlay/loading-overlay.service';
import { CostcentertreeService } from '../../../../shared/costcentertree/costcentertree.service';
import { IAddressDisplay, ICostcenter, ICostcenterAddress } from '../../../../shared/models/basic.model';
import { ISupplierContractCostcenter, ISupplierContractCostcenterCustomer, ISupplierCustomer } from '../../../shared/models/supplier.model';
import { SupplierService } from '../../../shared/services/supplier.service';

@UntilDestroy()
@Component({
    selector: 'app-conditions',
    templateUrl: './conditions.component.html',
    styleUrls: ['./conditions.component.scss'],
    standalone: false,
})
export class ConditionsComponent implements OnInit {
    private readonly utilityHelperService = inject(UtilityHelperService);
    readonly supplierService = inject(SupplierService);
    readonly costcentertreeService = inject(CostcentertreeService);
    readonly environmentService = inject(EnvironmentService);
    readonly ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);
    private readonly modalService = inject(ModalService);
    readonly formBuilder = inject(UntypedFormBuilder);
    readonly coreService = inject(CoreService);
    private readonly appService = inject(AppService);
    readonly loadingOverlayService = inject(LoadingOverlayService);

    public readonly customerModal = viewChild<TemplateRef<any>>('customerModal');

    public readonly updateable = input<boolean>(true);
    public readonly scc = input<ISupplierContractCostcenter>(undefined);
    public readonly indexSupplierContractCostcenters = input<number>(undefined);
    // @Input() sc: ISupplierContract = undefined;

    public costcenterAddressTitle: string = this.coreService.getMsgkey('mws.shippingaddresscostcenter', undefined);
    // showNewCustomer: boolean = false;
    public customerForm: UntypedFormGroup;

    public supplierCustomersFiltered: ISupplierCustomer[] = [];

    // convenience getter for easy access to form fields
    public get customerf() {
        return this.customerForm.controls;
    }

    public costcenter: ICostcenter;
    public costcenterAddresses: ICostcenterAddress[];
    public ccAddresses: IAddressDisplay[];

    private supplierCustomer: ISupplierCustomer;

    public ngOnInit() {
        const scc = this.scc();
        if (scc) {
            this.ccAddresses = [];
            this.costcenter = this.costcentertreeService.findCostcenterById(this.environmentService.userCostcenterTree, scc.costcenterId);
            this.costcentertreeService
                .getCostcenterAddressList(this.costcenter)
                .pipe(take(1))
                .subscribe((costcenterAdresses) => {
                    this.costcenterAddresses = costcenterAdresses;
                    this.costcenterAddresses.forEach((cca) => {
                        if (cca.address.addressTypeIsShipping) {
                            this.ccAddresses.push(cca.address);
                        }
                    });
                });
        }
        // if (this.sc) {
        //     this.supplierContractCostcenterCustomers = this.sc.supplierContractCostcenterCustomers
        //     this.costcenter = this.costcentertreeService.findCostcenterById(this.costcentertreeService.costcenterTree, this.sc.costcenterId);
        // }
    }

    public openCustomerModal() {
        this.initCustomerForm();
        // get actual customers of this Supplier !
        this.supplierService
            .getCustomersBySupplierId(this.supplierService.supplierId)
            .pipe(take(1))
            .subscribe(
                (supplierCustomers) => {
                    //
                    this.supplierCustomersFiltered = this.supplierService.supplierCustomers.filter(
                        (sc) =>
                            !this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers ||
                            !this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers.find((sccc) => {
                                //
                                if (
                                    sccc.supplierCustomer.id === sc.id &&
                                    sccc.supplierContractCostcenterId ===
                                        this.supplierService.supplierContractDetail.supplierContractCostcenters.find(
                                            (scc) => scc.costcenterId === this.costcenter.id,
                                        )?.id
                                )
                                    return true;
                                return false;
                            }),
                    );
                    //
                    this.modalService.openModal(this.customerModal());
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    public initCustomerForm() {
        this.customerForm = this.formBuilder.group({
            customerId: 0,
            customerNumber: [
                {
                    value: null,
                    disabled: false,
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierCustomer',
                        'customerNumber',
                        this.coreService.getMsgkey('mws.customernumber'),
                    ),
                ],
            ],
            customerName: [
                {
                    value: null,
                    disabled: false,
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierCustomer',
                        'customerName',
                        this.coreService.getMsgkey('mws.customername'),
                    ),
                ],
            ],
            currencyId: this.supplierService.supplierBasedata.currencyId
                ? this.supplierService.supplierBasedata.currencyId
                : this.environmentService.currencyList.find((cur) => cur.iso4217Code === 'EUR').id,
            changed: false,
        });
        this.customerForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            const tempCustomer: ISupplierCustomer = this.fillSupplierCustomerObj();
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierCustomer', tempCustomer);
            if (
                !this.customerForm.get('changed').touched &&
                result.isValid &&
                (this.customerForm.get('customerId').value > 0 ||
                    (this.customerForm.get('customerNumber').value &&
                        this.customerForm.get('customerNumber').value.length > 0 &&
                        this.customerForm.get('customerName').value &&
                        this.customerForm.get('customerName').value.length > 0))
            ) {
                this.customerForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.customerForm.get('changed').markAsUntouched();
            }
        });
    }

    fillSupplierCustomerObj(): ISupplierCustomer {
        return {
            id: this.supplierCustomer && this.supplierCustomer.id ? this.supplierCustomer.id : undefined,
            supplierId: this.supplierService.supplierId,
            customerName: this.customerForm.get('customerName').value,
            customerNumber: this.customerForm.get('customerNumber').value,
            currencyId: this.customerForm.get('currencyId').value,
            // isFavorite: false,
        };
    }
    // newCustomer() {
    //     // TODO
    //     console.log('conditions.component.ts - newCustomer');
    //     this.showNewCustomer = true;
    // }
    //
    // cancelNewCustomer() {
    //     // TODO
    //     console.log('conditions.component.ts - cancelNewCustomer')
    //     this.showNewCustomer = false;
    // }

    public changeCustomerSelectBox() {
        // TODO: fillCustomer OR clearCustomer
        if (this.customerForm.get('customerId').value > 0) {
            this.fillCustomer();
        } else {
            this.clearCustomer();
        }
    }

    public fillCustomer() {
        this.supplierCustomer = this.supplierService.supplierCustomers.find((_sc) => _sc.id === this.customerForm.get('customerId').value);
        this.customerForm.get('customerNumber').setValue(this.supplierCustomer.customerNumber);
        this.customerForm.get('customerName').setValue(this.supplierCustomer.customerName);
        this.customerForm.get('currencyId').setValue(this.supplierCustomer.currencyId);
        this.customerForm.get('customerNumber').disable();
        this.customerForm.get('customerName').disable();
        this.customerForm.get('currencyId').disable();
    }

    public clearCustomer() {
        this.customerForm.get('customerNumber').setValue(null);
        this.customerForm.get('customerName').setValue(null);
        this.customerForm.get('currencyId').setValue(this.environmentService.mandatorCurrency.id);
        this.customerForm.get('customerNumber').enable();
        this.customerForm.get('customerName').enable();
        this.customerForm.get('currencyId').enable();
    }

    public addCustomer() {
        if (
            this.customerForm.get('customerId').value > 0 ||
            (this.customerForm.get('customerNumber').value &&
                this.customerForm.get('customerNumber').value.length > 0 &&
                this.customerForm.get('customerName').value &&
                this.customerForm.get('customerName').value.length > 0)
        ) {
            //
            this.loadingOverlayService.startLoading();
            //
            const tempCustomer: ISupplierContractCostcenterCustomer = {
                id: undefined,
                supplierCustomer: {
                    id: this.customerForm.get('customerId').value === 0 ? undefined : this.customerForm.get('customerId').value,
                    supplierId: this.supplierService.supplierId,
                    customerName: this.customerForm.get('customerName').value,
                    customerNumber: this.customerForm.get('customerNumber').value,
                    currencyId: this.customerForm.get('currencyId').value, // this.supplierService.supplierBasedata.currencyId,
                    // isFavorite: false, // this.isFirstSupplierContractCostcenterCustomer(),
                },
                firstorderdate: null,
                minimumOrderValue: 0, // TODO
                // supplierContractCostcenterId: this.scc ? this.scc.id : undefined,
                supplierContractId: this.supplierService.supplierContract.id,
                supplierContractCostcenterId: this.scc().id,
                isFavorite: this.isFirstSupplierContractCostcenterCustomer(),
            };
            //
            this.supplierService
                .saveSupplierCostcenterCustomer(tempCustomer)
                .pipe(take(1))
                .subscribe(
                    (customer) => {
                        this.supplierService.supplierContractDetail.supplierContractCostcenters.forEach(
                            (supplierContractCostcenters) => {
                                const scc = this.scc();
                                if (scc && supplierContractCostcenters.id === scc.id) {
                                    // 1) AKTUALISIERE: this.supplierService.supplierContractDetail.supplierContractCostcenters
                                    supplierContractCostcenters.supplierContractCostcenterCustomers.push(customer);
                                    supplierContractCostcenters.supplierContractCostcenterCustomers =
                                        supplierContractCostcenters.supplierContractCostcenterCustomers.concat();
                                    // 2) AKTUALISIERE: this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers
                                    if (!this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers) {
                                        this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers =
                                            [] as ISupplierContractCostcenterCustomer[];
                                    }
                                    this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers.push(customer);
                                    return;
                                }
                            },
                            (err: IError) => {
                                this.utilityHelperService.handleComponentError(err);
                            },
                        );
                        this.supplierService.supplierContractDetail.supplierContractCostcenters =
                            this.supplierService.supplierContractDetail.supplierContractCostcenters.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
        } else {
            this.appService.showToast('error', 'ERROR:', 'CustomerForm NOT VALID');
        }
    }

    private isFirstSupplierContractCostcenterCustomer() {
        return !this.supplierService.supplierContractDetail.supplierContractCostcenterCustomers?.some(
            (sccc: ISupplierContractCostcenterCustomer) => sccc.supplierContractCostcenterId === this.scc().id,
        );
    }
}
